/**
 * RawMaterialOverview — clickable table showing all raw materials for a product.
 * Displayed when a finished product is selected. Manager can click a row to
 * select it as material for the session. Selected rows are highlighted.
 * Each material has a sub-row showing VB6 display balance per location.
 */
import React from 'react';
import { useLanguage } from '../../../../i18n/LanguageContext';
import { type RawMaterial, type LocationBalance } from '../../../../services/productionService';
import { formatYield, isYieldFallback } from '../../../../hooks/useYieldFactor';
import { Check, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';

interface RawMaterialOverviewProps {
  sortedRawMaterials: RawMaterial[];
  selectedItemKeys: Set<string>;
  onSelectMaterial: (material: RawMaterial) => void;
  loading: boolean;
  icsBalances?: Record<string, LocationBalance[]>;
  loadingIcsBalances?: boolean;
  sourceWarehouse?: string;
  stockAdjustments?: Map<string, number>;  // item_number → extra stock to add (e.g., session's lot-backed allocation)
}

const RawMaterialOverview: React.FC<RawMaterialOverviewProps> = ({
  sortedRawMaterials,
  selectedItemKeys,
  onSelectMaterial,
  loading,
  icsBalances,
  loadingIcsBalances,
  sourceWarehouse,
  stockAdjustments,
}) => {
  const { t } = useLanguage();
  const totalColumns = 7;

  if (loading) {
    return (
      <div className="text-sm text-gray-400 dark:text-gray-500 py-4 text-center">
        {t('common.loading') || 'Loading...'}
      </div>
    );
  }

  if (sortedRawMaterials.length === 0) return null;

  return (
    <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-100 dark:bg-gray-700/70 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
            <th className="px-3 py-2 text-left w-8"></th>
            <th className="px-3 py-2 text-left">{t('supervisor.material') || 'Material'}</th>
            <th className="px-3 py-2 text-right">{t('operator.yield') || 'Yield'}</th>
            <th className="px-3 py-2 text-right">{t('supervisor.laborFactor') || 'Labor'}</th>
            <th className="px-3 py-2 text-right">{t('supervisor.meanProduction') || 'Mean Prod'}</th>
            <th className="px-3 py-2 text-right">{t('supervisor.unitWeight') || 'Unit Wt'}</th>
            <th className="px-3 py-2 text-center">{t('supervisor.status') || 'Status'}</th>
          </tr>
        </thead>
        <tbody>
          {sortedRawMaterials.map((material, idx) => {
            const key = `${material.item_number}|${material.transfer_number || ''}|${material.location_code || ''}`;
            const isSelected = selectedItemKeys.has(key) || selectedItemKeys.has(`${material.item_number}||`);
            const yieldValue = material.yield_factor ? parseFloat(String(material.yield_factor)) : 0;
            const yieldDisplay = yieldValue ? formatYield(yieldValue, undefined, material.yield_factor_source) : '-';
            const isFallback = isYieldFallback(material.yield_factor_source);
            const stock = (parseFloat(String(material.current_stock)) || 0) + (stockAdjustments?.get(material.item_number) || 0);
            const locations = icsBalances?.[material.item_number];
            const visibleLocations = locations?.filter(l =>
              l.locationCode !== 'C' && (l.displayBalance !== 0 || l.locationCode === sourceWarehouse)
            );
            const labourFactor = material.labour_factor;

            return (
              <React.Fragment key={`${key}-${idx}`}>
                <tr
                  onClick={() => onSelectMaterial(material)}
                  className={`cursor-pointer border-t border-gray-100 dark:border-gray-600/50 transition-colors ${
                    isSelected
                      ? 'bg-indigo-50 dark:bg-indigo-900/30'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                  }`}
                >
                  {/* Checkmark + Preference */}
                  <td className="px-3 py-2 text-center">
                    {isSelected
                      ? <Check className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                      : material.preference
                        ? <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">{material.preference}</span>
                        : null
                    }
                  </td>

                  {/* Item # + Name */}
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900 dark:text-white text-sm">
                      {material.item_number}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[220px]">
                      {material.item_english_name}
                      {material.item_chinese_name ? ` / ${material.item_chinese_name}` : ''}
                    </div>
                  </td>

                  {/* Yield */}
                  <td className={`px-3 py-2 text-right text-sm font-medium ${
                    isFallback
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-gray-900 dark:text-white'
                  }`}>
                    {yieldDisplay}
                  </td>

                  {/* Labor Factor */}
                  <td className="px-3 py-2 text-right text-sm text-gray-600 dark:text-gray-300">
                    {parseFloat(String(labourFactor ?? 0)).toFixed(2)}
                  </td>

                  {/* Mean Production */}
                  <td className="px-3 py-2 text-right text-sm text-gray-600 dark:text-gray-300">
                    {material.mean_output_lbs
                      ? `${parseFloat(String(material.mean_output_lbs)).toFixed(2)} lb`
                      : '-'}
                  </td>

                  {/* Unit Weight */}
                  <td className="px-3 py-2 text-right text-sm text-gray-600 dark:text-gray-300">
                    {material.unit_weight ? `${Number(material.unit_weight).toFixed(2)} lb` : '-'}
                  </td>

                  {/* Status */}
                  <td className="px-3 py-2 text-center">
                    {material.is_on_hold ? (
                      <AlertTriangle className="w-5 h-5 text-amber-500 dark:text-amber-400 mx-auto" />
                    ) : material.has_lot_inventory ? (
                      <CheckCircle className="w-5 h-5 text-green-500 dark:text-green-400 mx-auto" />
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-500">-</span>
                    )}
                  </td>
                </tr>

                {/* VB6 Balance sub-row — display balance per location */}
                {(locations || loadingIcsBalances) && (
                  <tr className={`border-t border-gray-50 dark:border-gray-700/30 ${
                    isSelected ? 'bg-indigo-50/50 dark:bg-indigo-900/20' : ''
                  }`}>
                    <td></td>
                    <td colSpan={totalColumns - 1} className="px-3 py-1.5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className="text-sm font-medium text-gray-500 dark:text-gray-400 mr-1">
                            {t('supervisor.icsBalance') || 'VB6 Balance'}:
                          </span>
                          {loadingIcsBalances && !locations ? (
                            <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                          ) : visibleLocations && visibleLocations.length > 0 ? (
                            visibleLocations.map(loc => {
                              const cp = loc.displayCasesPieces;
                              const isSource = sourceWarehouse === loc.locationCode;
                              return (
                                <span
                                  key={loc.locationCode}
                                  className={`inline-flex items-center text-sm px-2 py-0.5 rounded ${
                                    isSource ? 'ring-2 ring-indigo-500 dark:ring-indigo-400 font-semibold ' : ''
                                  }${
                                    loc.displayBalance > 0
                                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                  }`}
                                >
                                  <span className="font-medium">{loc.locationCode}</span>
                                  <span className="mx-0.5">:</span>
                                  <span>{cp ? `${cp.cases} CS` : loc.displayBalance.toLocaleString()}</span>
                                </span>
                              );
                            })
                          ) : (
                            <span className="text-sm text-gray-400 dark:text-gray-500">
                              {t('common.none') || 'None'}
                            </span>
                          )}
                        </div>
                        <span className={`text-sm font-medium whitespace-nowrap ml-3 ${
                          stock > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'
                        }`}>
                          {t('supervisor.availableAtHte') || 'Available (@HTE)'}: {stock > 0 ? `${stock.toFixed(2)} lb` : '0 lb'}
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t border-gray-300 dark:border-gray-500">
            <td colSpan={totalColumns} className="px-3 py-2">
              <div className="flex justify-between items-center text-sm">
                <span className="font-medium text-gray-600 dark:text-gray-300">
                  {t('supervisor.totalAvailableAtHte') || 'Total Available (@HTE)'}
                </span>
                <span className={`font-semibold ${
                  sortedRawMaterials.reduce((sum, m) => sum + (parseFloat(String(m.current_stock)) || 0) + (stockAdjustments?.get(m.item_number) || 0), 0) > 0
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-gray-400 dark:text-gray-500'
                }`}>
                  {sortedRawMaterials.reduce((sum, m) => sum + (parseFloat(String(m.current_stock)) || 0) + (stockAdjustments?.get(m.item_number) || 0), 0).toFixed(2)} lb
                </span>
              </div>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default RawMaterialOverview;
